import { Button, Grid, LinearProgress, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "../../auth/AuthContext";
import DownloadParticipantListVersionButton from "./DownloadParticipantListVersionButton";
import ParticipantListEditorActionBar from "./ParticipantListEditorActionBar";
import ParticipantListLinksModal from "./ParticipantListLinksModal";
import ParticipantListMainContent from "./ParticipantListMainContent";
import ParticipantListStatusDialog from "./ParticipantListStatusDialog";
import ParticipantListVersionStatusRow from "./ParticipantListVersionStatusRow";
import ParticipantListViewActionBar from "./ParticipantListViewActionBar";
import SendParticipantListToProviders from "./SendParticipantListToProviders";
import type { ParticipantListStatus } from "./participantListStatus";
import { useParticipantListsPageState } from "./useParticipantListsPageState";

interface ShareButtonProps {
  readonly onClick: () => void;
}

const ShareButton: FC<ShareButtonProps> = ({ onClick }) => (
  <Button variant="outlined" color="secondary" onClick={onClick}>
    Udostępnij
  </Button>
);

const OrderParticipantLists: FC = () => {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const { context: authContext } = useAuthContext();

  const {
    template,
    isBootstrapping,
    participantLists,
    updateStatus,
    sendToProviders,
    editMode,
    setSelectedVersionIdx,
    editor,
    lastSavedList,
    showEditor,
    resolvedVersionIdx,
    selectedList,
    previousList,
    noParticipantsRequired,
    exportTargets,
    handleEdit,
    handleCancelEdit,
    handleSave,
    handleUploadSpreadsheet,
  } = useParticipantListsPageState(orderId!);

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [participantListLinksModalOpen, setParticipantListLinksModalOpen] = useState(false);

  const isAdminOrAgent = authContext?.role === "ADMIN" || authContext?.role === "AGENT";

  if (isBootstrapping) {
    return (
      <Grid container>
        <Grid size={12}>
          <LinearProgress data-testid="loading-indicator" />
        </Grid>
      </Grid>
    );
  }

  if (!template) {
    return (
      <Grid
        container
        sx={{
          marginTop: 2,
          marginBottom: 2,
        }}
      >
        <Typography variant="h5">Takie zamówienie nie istnieje</Typography>
      </Grid>
    );
  }

  const orderUserFriendlyId = template.order.userFriendlyId;
  const backToOrder = () => navigate(`/orders/${orderId}`);

  const viewActions = selectedList ? (
    <>
      <DownloadParticipantListVersionButton
        buttonText="Pobierz wersję"
        spreadsheetExportTargets={template.spreadsheetExportTargets}
        orderId={orderId!}
        listVersionNumber={resolvedVersionIdx + 1}
        userFriendlyOrderId={orderUserFriendlyId}
      />
      <Button variant="outlined" color="secondary" onClick={() => setStatusDialogOpen(true)}>
        Zmień status
      </Button>
      <ShareButton onClick={() => setParticipantListLinksModalOpen(true)} />
      {isAdminOrAgent ? (
        <SendParticipantListToProviders
          exportTargets={exportTargets}
          participantListStatus={selectedList.status}
          listVersionNumber={resolvedVersionIdx + 1}
          sendToProviders={sendToProviders}
        />
      ) : null}
    </>
  ) : null;

  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 2,
        paddingBottom: 2,
      }}
    >
      <Grid size={12}>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "baseline",
          }}
        >
          <Typography variant="h6">Zamówienie {orderUserFriendlyId}</Typography>
          <Typography variant="body2">Lista uczestników</Typography>
        </Stack>
      </Grid>
      {!showEditor && participantLists.length > 0 ? (
        <ParticipantListVersionStatusRow
          resolvedVersionIdx={resolvedVersionIdx}
          participantLists={participantLists}
          selectedList={selectedList}
          onVersionChange={setSelectedVersionIdx}
        />
      ) : null}
      <Grid size={12}>
        <ParticipantListMainContent
          noParticipantsRequired={noParticipantsRequired}
          showEditor={showEditor}
          template={template}
          lastSavedList={lastSavedList}
          editor={editor}
          selectedList={selectedList}
          previousList={previousList}
          showDiffs={resolvedVersionIdx > 0}
        />
      </Grid>
      {!noParticipantsRequired ? (
        <Grid
          size={12}
          container
          sx={{
            justifyContent: "flex-end",
          }}
        >
          {showEditor ? (
            <ParticipantListEditorActionBar
              orderId={orderId!}
              orderUserFriendlyId={orderUserFriendlyId}
              onBackToOrder={backToOrder}
              editMode={editMode}
              onCancelEdit={handleCancelEdit}
              onReset={editor.handleReset}
              onSave={handleSave}
              onUploadSpreadsheet={handleUploadSpreadsheet}
              submitEnabled={editor.submitEnabled}
              shareButton={<ShareButton onClick={() => setParticipantListLinksModalOpen(true)} />}
            />
          ) : (
            <ParticipantListViewActionBar onBackToOrder={backToOrder} middleContent={viewActions} onEdit={handleEdit} />
          )}
        </Grid>
      ) : null}
      {selectedList ? (
        <ParticipantListStatusDialog
          open={statusDialogOpen}
          status={selectedList.status}
          onCancel={() => setStatusDialogOpen(false)}
          onConfirm={async (status: ParticipantListStatus) => {
            await updateStatus(resolvedVersionIdx + 1, status);
            setStatusDialogOpen(false);
          }}
        />
      ) : null}
      {orderId ? (
        <ParticipantListLinksModal
          orderId={orderId}
          open={participantListLinksModalOpen}
          onClose={() => setParticipantListLinksModalOpen(false)}
        />
      ) : null}
    </Grid>
  );
};

export default OrderParticipantLists;
